import ReactDOM from 'react-dom/client';
import './scss/main.scss';
import './i18n';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/montserrat/300.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/700.css';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, Route, RouteObject, Routes } from 'react-router-dom';
import { AuthStoreProvider } from './stores/AuthStore/AuthStore';
import { AlertStoreProvider } from './stores/AlertStore/AlertStore';
import { ConfettiStoreProvider } from './stores/ConfettiStore/ConfettiStore';
import { DialogStoreProvider } from './stores/DialogStore/DialogStore';
import CustomDialog from './components/custom-dialog/CustomDialog';
import { ROUTES } from './constants/UiRoutes';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { OrganizationProvider } from './stores/OrganizationStore/OrganizationStore';
import { ActiveUsersStoreProvider } from './stores/ActiveUsersStore/ActiveUsersStore';
import CustomNotification from './components/custom-notification/CustomNotification';
import ConfettiCanvas from './components/confetti/ConfettiCanvas';
import { PaymentStoreProvider } from './stores/PaymentStore/PaymentStore';
import PaymentFlowLoader from './pages/lemonsqueezy-payment/paymentFlowLoader';
import { UpgradeRequestStoreProvider } from './stores/UpgradeRequestStore/UpgradeRequestStore';
import { OverviewStoreProvider } from './stores/OverviewStore/OverviewStore';
import { UnifiedDigestStoreProvider } from './stores/UnifiedDigestStore/UnifiedDigestStore';
import { ConnectorsStoreProvider } from './stores/ConnectorsStore/ConnectorsStore';
import { BrainMemoryStoreProvider } from './stores/BrainMemoryStore/BrainMemoryStore';
import { UserSettingsStoreProvider } from './stores/UserSettingsStore/UserSettingsStore';
import { OauthScopesStoreProvider } from './stores/OauthScopesStore/OauthScopesStore';
import { SuperAdminMemoryStoreProvider } from './stores/SuperAdminMemoryStore/SuperAdminMemoryStore';
import { TagStoreProvider } from './modules/tag/TagStore';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CookieConsentBanner from './components/cookie-consent/CookieConsentBanner';
import AxiosInterceptor from './components/axios-interceptor/AxiosInterceptor';
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { CONFIG, IS_PROD } from './constants/config';
import LoadingIndicator from 'src/components/loader';
import { Box } from '@mui/material';
import { LanguageProvider } from './stores/LanguageStore/LanguageStore';
import { ReportGroupSummaryStoreProvider } from './stores/ReportGroupSummaryStore/ReportGroupSummaryStore';
import { SpaceStoreProvider } from './stores/SpaceStore/SpaceStore';
import { MarketplaceReviewStoreProvider } from './stores/MarketplaceReviewStore/MarketplaceReviewStore';
import { UserStoreProvider } from './stores/UserStore/UserStore';
import { createStatsigPlugins } from './utils/createStatsigPlugins';
import DashboardThemeProvider from './components/dashboard-theme-provider/DashboardThemeProvider';
import ChunkRecoveryBoundary from './components/chunk-recovery-boundary/ChunkRecoveryBoundary';

const AppWithRouter = () => {
  const { client } = useClientAsyncInit(
    CONFIG.STATSIG_CLIENT_SECRET,
    { userID: CONFIG.STATSIG_CLIENT_USER_ID },
    {
      loggingEnabled: IS_PROD ? 'browser-only' : 'disabled',
      plugins: IS_PROD
        ? createStatsigPlugins(CONFIG.STATSIG_CONSOLE_LOG_CAPTURE_SETTINGS)
        : [],
    },
  );

  const renderRoutes = (routes: RouteObject[]) => {
    return routes.map((route) => (
      <Route key={route.path} path={route.path} element={route.element}>
        {route.children ? renderRoutes(route.children) : null}
      </Route>
    ));
  };

  return (
    <StatsigProvider
      client={client}
      loadingComponent={
        <Box sx={{ height: '100vh', width: '100vw' }}>
          <LoadingIndicator />
        </Box>
      }
    >
      <LanguageProvider>
        <AuthStoreProvider>
          <UserSettingsStoreProvider>
            <OauthScopesStoreProvider>
              <OrganizationProvider>
                <SpaceStoreProvider>
                  <UserStoreProvider>
                    <ActiveUsersStoreProvider>
                      <TagStoreProvider>
                        <AlertStoreProvider>
                          <MarketplaceReviewStoreProvider>
                            <ReportGroupSummaryStoreProvider>
                              <ConfettiStoreProvider>
                                <DialogStoreProvider>
                                  <PaymentStoreProvider>
                                    <OverviewStoreProvider>
                                      <UnifiedDigestStoreProvider>
                                        <UpgradeRequestStoreProvider>
                                          <ConnectorsStoreProvider>
                                            <BrainMemoryStoreProvider>
                                              <SuperAdminMemoryStoreProvider>
                                                <DashboardThemeProvider>
                                                  <BrowserRouter>
                                                    <AxiosInterceptor>
                                                      <CookieConsentBanner />
                                                      <Routes>
                                                        {renderRoutes(ROUTES)}
                                                      </Routes>
                                                      <CustomDialog />
                                                      <CustomNotification />
                                                      <ConfettiCanvas />
                                                      <PaymentFlowLoader />
                                                    </AxiosInterceptor>
                                                  </BrowserRouter>
                                                </DashboardThemeProvider>
                                              </SuperAdminMemoryStoreProvider>
                                            </BrainMemoryStoreProvider>
                                          </ConnectorsStoreProvider>
                                        </UpgradeRequestStoreProvider>
                                      </UnifiedDigestStoreProvider>
                                    </OverviewStoreProvider>
                                  </PaymentStoreProvider>
                                </DialogStoreProvider>
                              </ConfettiStoreProvider>
                            </ReportGroupSummaryStoreProvider>
                          </MarketplaceReviewStoreProvider>
                        </AlertStoreProvider>
                      </TagStoreProvider>
                    </ActiveUsersStoreProvider>
                  </UserStoreProvider>
                </SpaceStoreProvider>
              </OrganizationProvider>
            </OauthScopesStoreProvider>
          </UserSettingsStoreProvider>
        </AuthStoreProvider>
      </LanguageProvider>
    </StatsigProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const nonce = document.getElementById('hanabi-inline-csp-nonce')?.nonce;

if (!nonce) {
  throw new Error('nonce value is not set by the server.');
}

const emotionCache = createCache({
  key: 'css-cache-key',
  nonce: nonce,
  prepend: true,
});

root.render(
  <CacheProvider value={emotionCache}>
    <ChunkRecoveryBoundary>
      <AppWithRouter />
    </ChunkRecoveryBoundary>
  </CacheProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
